import * as XLSX from "xlsx";
import {
  CATEGORIES,
  TIME_FRAMES,
  getWholesalerColumns,
  getWholesalerDayTransactionColumns,
  wholesalerMonthColumns,
  wholesalerWeeklyColumns,
} from "./columns";
import { parse, isValid, format } from "date-fns";

const formatCustomDate = (value, type) => {
  if (!value) return "";
  try {
    switch (type) {
      case "day": {
        const formats = [
          "yyyy-MM-dd",
          "MM/dd/yyyy",
          "yyyy/MM/dd",
          "yyyy-M-d",
          "dd-MM-yyyy",
        ];
        for (let fmt of formats) {
          const date = parse(value, fmt, new Date());
          if (isValid(date)) return format(date, "dd MMM yyyy");
        }
        return value;
      }
      case "month": {
        if (/^\d{4}-\d{2}$/.test(value)) {
          const date = parse(value, "yyyy-MM", new Date());
          return isValid(date) ? format(date, "MMMM yyyy") : value;
        }
        if (/^\d{6}$/.test(value)) {
          const year = value.slice(0, 4);
          const month = value.slice(4);
          return `${format(new Date(year, month - 1), "MMMM")} ${year}`;
        }
        return value;
      }
      case "week": {
        const match = value.match(/^(\d{4})-W(\d{1,2})$/);
        if (match) {
          return `Week ${match[2]}, ${match[1]}`;
        }
        if (/^\d{6}$/.test(value)) {
          return getFormattedWeek(value);
        }
        if (typeof value === "string" && value.includes("-")) {
          const parts = value.split("-");
          if (parts.length === 2) {
            const [first, second] = parts;
            if (first.length === 4) {
              return `Week ${second}, ${first}`;
            } else {
              return `Week ${first}, ${second}`;
            }
          }
        }
        if (/^\d{1,2}$/.test(value)) {
          return `Week ${value}`;
        }
        return value;
      }
      default:
        return value;
    }
  } catch {
    return value;
  }
};

const formatNumberWithDecimals = (value, decimalPlaces = 2) => {
  if (value === null || value === undefined || value === "") return 0;
  const num = parseFloat(value);
  return isNaN(num) ? 0 : num.toFixed(decimalPlaces);
};

const shouldFormatAsDecimal = (key) => {
  const decimalFields = [
    "Transactions",
    "WeeklyTransactions",
    "MonthlyTransactions",
    "Shots",
    "total",
    "Total Amount",
    "ordered",
  ];
  return decimalFields.includes(key);
};

export const prepareExcelData = (filteredRows, selectedCategory, timeFrame) => {
  let dataForExport = [];

  const getFieldToHeaderMap = () => {
    const columns = [...getWholesalerColumns(() => {})];

    if (timeFrame === TIME_FRAMES.DAY) {
      columns.push(...getWholesalerDayTransactionColumns);
    } else if (timeFrame === TIME_FRAMES.MONTH) {
      columns.push(...wholesalerMonthColumns);
    } else if (timeFrame === TIME_FRAMES.WEEK) {
      columns.push(...wholesalerWeeklyColumns);
    }

    const map = {};
    columns.forEach((col) => {
      if (col.field && col.headerName && col.field !== "action") {
        map[col.field] = col.headerName;
      }
    });

    map.id = "S.No";
    return map;
  };

  const fieldToHeaderMap = getFieldToHeaderMap();

  // Process CUSTOMER data
  if (selectedCategory === CATEGORIES.CUSTOMER) {
    filteredRows.forEach((row) => {
      dataForExport.push({
        id: dataForExport.length + 1,
        Name: row.name,
        Email: row.email,
        Mobile: row.mobile,
        "Order Brand": row.orderBrand,
        "Ordered Quantity": row.ordered,
        "Total Amount": formatNumberWithDecimals(row.total),
        "Wholesaler Name": row.wholesaler_name,
        "Order Date": formatCustomDate(row.date, "day"),
      });
    });
  }

  // Process WHOLESALER data
  if (selectedCategory === CATEGORIES.WHOLESALER) {
    filteredRows.forEach((row) => {
      const name = row.Name;
      const address = row.Address;
      const email = row.Email;
      const phone = row.PhoneNo;

      if (timeFrame === TIME_FRAMES.DAY && Array.isArray(row.dayWiseReport)) {
        row.dayWiseReport.forEach((report) => {
          dataForExport.push({
            id: dataForExport.length + 1,
            Name: name,
            Email: email,
            PhoneNo: phone,
            Address: address,
            Date: formatCustomDate(report.date || "N/A", "day"),
            MostSoldBrand: report.mostOrderedBrand || "N/A",
            Amount: formatNumberWithDecimals(report.transactions || 0),
            BottlesSold: report.totalBottlesSold || 0,
          });
        });
      }

      if (timeFrame === TIME_FRAMES.WEEK && Array.isArray(row.weekWiseReport)) {
        row.weekWiseReport.forEach((report) => {
          dataForExport.push({
            id: dataForExport.length + 1,
            Name: name,
            Email: email,
            PhoneNo: phone,
            Address: address,
            Week: formatCustomDate(report.week || "N/A", "week"),
            Amount: formatNumberWithDecimals(
              report.weeklyTransactions || 0
            ),
          });
        });
      }

      if (
        timeFrame === TIME_FRAMES.MONTH &&
        Array.isArray(row.monthWiseReport)
      ) {
        row.monthWiseReport.forEach((report) => {
          dataForExport.push({
            id: dataForExport.length + 1,
            Name: name,
            Email: email,
            PhoneNo: phone,
            Address: address,
            Month: formatCustomDate(report.month || "N/A", "month"),
            Amount: formatNumberWithDecimals(
              report.monthlyTransactions || 0
            ),
          });
        });
      }
    });
  }

  // Process BAR data
  if (selectedCategory === CATEGORIES.BAR_CLUB) {
    filteredRows.forEach((row) => {
      const name = row.Name;
      const address = row.Address;
      const email = row.Email;
      const phone = row.Contact;

      if (timeFrame === TIME_FRAMES.DAY && Array.isArray(row.dayWiseReport)) {
        row.dayWiseReport.forEach((report) => {
          dataForExport.push({
            id: dataForExport.length + 1,
            Name: name,
            Email: email,
            Contact: phone,
            Address: address,
            Date: formatCustomDate(report.date || "N/A", "day"),
            MostSoldBrand: report.mostOrderedBrand || "N/A",
            TotalShots: report.totalShots || 0,
          });
        });
      }

      if (timeFrame === TIME_FRAMES.WEEK && Array.isArray(row.weekWiseReport)) {
        row.weekWiseReport.forEach((report) => {
          dataForExport.push({
            id: dataForExport.length + 1,
            Name: name,
            Email: email,
            Contact: phone,
            Address: address,
            Week: formatCustomDate(report.week || "N/A", "week"),
            MostSoldBrand: report.mostOrderedBrand || "N/A",
            TotalShots: report.totalShots || 0,
          });
        });
      }

      if (
        timeFrame === TIME_FRAMES.MONTH &&
        Array.isArray(row.monthWiseReport)
      ) {
        row.monthWiseReport.forEach((report) => {
          dataForExport.push({
            id: dataForExport.length + 1,
            Name: name,
            Email: email,
            Contact: phone,
            Address: address,
            Month: formatCustomDate(report.month || "N/A", "month"),
            MostSoldBrand: report.mostOrderedBrand || "N/A",
            TotalShots: report.totalShots || 0,
          });
        });
      }
    });
  }

  // Remove unwanted keys
  const unwantedKeys = [
    "originalItem",
    "dayWiseReport",
    "weekWiseReport",
    "monthWiseReport",
    "isChild",
    "parentId",
    "dataType",
    "orderId",
    "orderNo",
  ];

  dataForExport = dataForExport.map((row) => {
    unwantedKeys.forEach((key) => delete row[key]);
    return row;
  });

  dataForExport = dataForExport.map((row) => {
    const newRow = {};
    Object.keys(row).forEach((key) => {
      let value = row[key];

      if (key === "Date" && typeof value === "string" && !value.includes(" ")) {
        value = formatCustomDate(value, "day");
      } else if (
        key === "Week" &&
        typeof value === "string" &&
        !value.includes("Week")
      ) {
        value = formatCustomDate(value, "week");
      } else if (
        key === "Month" &&
        typeof value === "string" &&
        !value.includes(" ")
      ) {
        value = formatCustomDate(value, "month");
      }

      if (shouldFormatAsDecimal(key)) {
        value = formatNumberWithDecimals(value);
      }

      const headerName = fieldToHeaderMap[key] || key;

      if (shouldFormatAsDecimal(headerName)) {
        value = formatNumberWithDecimals(value);
      }

      newRow[headerName] = value;
    });
    return newRow;
  });

  const worksheet = XLSX.utils.json_to_sheet(dataForExport);
  const workbook = XLSX.utils.book_new();
  XLSX.utils.book_append_sheet(workbook, worksheet, "Sales Report");

  return workbook;
};

export const getFormattedWeek = (weekStr) => {
  if (!weekStr) return "N/A";

  if (typeof weekStr === "string" && weekStr.includes("-")) {
    const [year, week] = weekStr.split("-");
    return `Week ${week}, ${year}`;
  }

  if (weekStr.length === 6) {
    const year = weekStr.slice(0, 4);
    const week = weekStr.slice(4);
    return `Week ${week}, ${year}`;
  }

  return "N/A";
};
